import { Box, Button, Stack, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import EditIcon from '../../../../assets/images/edit-pencil.svg?react'
import { Contacts, GBAddressCountry, Location, USAddressCountry } from '../../../../api/models'
import { Grid } from '@mui/system'
import { numberToWord } from '../../../../utils/utils'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'
import { WRITE_LOCATION_CONTACTS_SCOPE } from '../../../../constants'
import { useMonitoringServiceProviderLocation, useUpdateContacts } from '../../../../api/apiQueries'
import { formatPhoneNumberForDisplay } from './phoneNumberUtils'
import { isProfessionallyMonitored } from './locationUtils'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { useBlocker } from 'react-router-dom'
import { EmergencyContactsEditView } from './EmergencyContactsEditView'
import { InterruptionPanel } from '../../../monitoring-moderation/CameraOverviewPage/InterruptionPanel'
import { ContactCard } from './ContactCard'
import { useSetAtom } from 'jotai'
import { provisioningWorkflowStartedByLocationAtom } from '../../../../atoms'
import { LoggerName, namedLogger } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.EmergencyContacts)

interface LocationInfoProps {
  location: Location
}

export const EmergencyContacts: FC<LocationInfoProps> = ({ location }) => {
  const authContext = useAuthenticationContext()
  const setProvisioningWorkflowStarted = useSetAtom(provisioningWorkflowStartedByLocationAtom)

  const contacts: Contacts = location?.contacts ?? { primaryContacts: [], secondaryContacts: [] }

  const mspLocationQuery = useMonitoringServiceProviderLocation(location.id)
  const mspLocation = mspLocationQuery.data

  const mspContacts = mspLocation?.contacts
  const mspIdentifier = mspLocation?.monitoringServiceProviderId || 'MSP'

  const disabledFieldStyle = !isProfessionallyMonitored(location) ? { color: 'var(--text-disabled)' } : {}

  const maxNumberOfSecondaryContacts = 5

  const isGBLocation = location?.address?.country === GBAddressCountry.GB
  const phoneCountry = (location.address?.country as GBAddressCountry | USAddressCountry) ?? USAddressCountry.US

  const maxNumberOfPrimaryContacts = 2

  const primaryContactsDifference = mspContacts?.primary.length ?? 0 - contacts.primaryContacts.length
  const secondaryContactsDifference = mspContacts?.secondary.length ?? 0 - contacts.secondaryContacts.length

  const getPrimaryContactName = (contact: Contacts['primaryContacts'][number] | undefined) => {
    const fullName = `${contact?.name?.firstName ?? ''} ${contact?.name?.lastName ?? ''}`.trim()
    if (fullName === '') {
      return 'Empty Contact'
    }
    return fullName
  }

  const getSecondaryContactName = (contact: Contacts['secondaryContacts'][number] | undefined) => {
    if (!contact?.name || contact.name.trim() === '') return 'Empty Contact'
    return contact.name
  }

  const [showNavigationInterruption, setShowNavigationInterruption] = useState(false)
  const [isInContactsEditMode, setIsInContactsEditMode] = useState(false)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => isInContactsEditMode && currentLocation.pathname !== nextLocation.pathname)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Previously suppressed `react-hooks/set-state-in-effect` here. Biome 1.9.4 has no equivalent rule, and this effect intentionally opens interruption UI when navigation becomes blocked.
      setShowNavigationInterruption(true)
    }
  }, [blocker.state])

  const { toast } = useSnackbar()

  const onEditClick = useCallback(() => {
    setIsInContactsEditMode(true)
  }, [])

  const handleCancel = useCallback(() => {
    setIsInContactsEditMode(false)
  }, [])

  const updateContactsMutation = useUpdateContacts(location.id)

  const handleContactsSave = useCallback(
    async (contacts: Contacts) => {
      let attempts = 0
      let success = false

      while (attempts < 3 && !success) {
        try {
          await updateContactsMutation.mutateAsync(contacts)
          success = true
        } catch (err) {
          logger.error(`Attempt ${attempts + 1} to update contacts failed`, err as Error)
          attempts += 1
          if (attempts < 3) {
            // Add a delay before retrying
            await new Promise(res => setTimeout(res, 1000))
          }
        }
      }

      if (success) {
        toast('Contacts updated successfully.', { variant: 'success' })
        setProvisioningWorkflowStarted(previousState => ({ ...previousState, [location.id]: true }))
      } else {
        toast(`Failed to update contacts.`, { variant: 'error' })
        setProvisioningWorkflowStarted(previousState => ({ ...previousState, [location.id]: false }))
      }
      setIsInContactsEditMode(false)
    },
    [location.id, setProvisioningWorkflowStarted, toast, updateContactsMutation]
  )

  const handleNavigationInterruption = useCallback(
    (confirmed: boolean) => {
      if (confirmed) {
        setIsInContactsEditMode(false)
        blocker.proceed?.()
      } else {
        blocker.reset?.()
      }
      setShowNavigationInterruption(false)
    },
    [blocker]
  )

  return (
    <>
      {isInContactsEditMode ? (
        <EmergencyContactsEditView
          location={location}
          contacts={contacts}
          onCancel={handleCancel}
          onSave={handleContactsSave}
          loading={updateContactsMutation.isPending}
        />
      ) : (
        <Stack
          direction="column"
          spacing={'var(--spacing-m)'}
          sx={{ backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--border-radius-l)', padding: 'var(--spacing-m)' }}
        >
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="column" spacing={'var(--spacing-xs)'}>
              <Typography variant={'h2'} fontSize={'var(--size-xl)'} sx={{ fontWeight: '700' }}>
                Emergency Contacts
              </Typography>
              <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>
                Manage the people who will be contacted if there's an alarm.
              </Typography>
            </Stack>
            {isProfessionallyMonitored(location) && authContext.has(WRITE_LOCATION_CONTACTS_SCOPE) && (
              <Box>
                <Button
                  variant="text"
                  size="large"
                  onClick={onEditClick}
                  data-testid="emergency-contacts-edit-button"
                  sx={{
                    height: 'var(--spacing-3xl)',
                    fontSize: 'var(--size-s)',
                    gap: 'var(--spacing-xs)'
                  }}
                >
                  <Typography>Edit</Typography>
                  <EditIcon />
                </Button>
              </Box>
            )}
          </Stack>
          <Stack direction="column" spacing={'var(--spacing-xs)'} sx={{ paddingBlockEnd: 'var(--spacing-m)' }}>
            <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-s)', ...disabledFieldStyle }}>Primary Contacts</Typography>
            <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>
              We will call up to {numberToWord(maxNumberOfPrimaryContacts)} people before requesting emergency services to avoid dispatch for false
              alarms.
            </Typography>
            <Grid
              container
              direction="row"
              spacing={'var(--spacing-s)'}
              sx={{
                paddingBlockStart: 'var(--spacing-m)',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: 'var(--border-radius-l)'
              }}
            >
              {Array.from({ length: maxNumberOfPrimaryContacts }, (_, index) => {
                const contact = contacts.primaryContacts[index]
                const mspContact = mspContacts?.primary[index]
                const isGbSecondPrimaryContact = isGBLocation && index === 1
                const primaryName = getPrimaryContactName(contact)
                const isPrimaryNameEmpty = primaryName === 'Empty Contact'
                const shouldReserveEmptyPrimaryNameLine = isGbSecondPrimaryContact && isPrimaryNameEmpty
                const displayedPrimaryName = shouldReserveEmptyPrimaryNameLine ? '' : primaryName
                const isEmptyWithDifference =
                  !contact &&
                  primaryContactsDifference > 0 &&
                  index >= contacts.primaryContacts.length &&
                  index < contacts.primaryContacts.length + primaryContactsDifference
                return (
                  <Grid size={4} key={index}>
                    <ContactCard
                      data-testid={`primary-contact-${index}`}
                      label={`Primary Contact ${index + 1}`}
                      contactName={displayedPrimaryName}
                      shouldReserveEmptyPrimaryNameLine={shouldReserveEmptyPrimaryNameLine}
                      contactPhoneNumber={formatPhoneNumberForDisplay(contact?.phone?.number, phoneCountry)}
                      isProfessionallyMonitored={isProfessionallyMonitored(location)}
                      additionalContactTooltip={
                        isEmptyWithDifference && mspContact
                          ? `${mspIdentifier} has an additional contact: ${mspContact.name} (${mspContact.phone.number})`
                          : undefined
                      }
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Stack>
          <Stack direction="column" spacing={'var(--spacing-xs)'}>
            <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-s)', ...disabledFieldStyle }}>Secondary Contacts</Typography>
            <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>
              We will call up to {numberToWord(maxNumberOfSecondaryContacts)} people after requesting emergency services.
            </Typography>
            <Grid
              container
              direction="row"
              spacing={'var(--spacing-s)'}
              sx={{
                paddingBlockStart: 'var(--spacing-m)',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: 'var(--border-radius-l)'
              }}
            >
              {Array.from({ length: maxNumberOfSecondaryContacts }, (_, index) => {
                const contact = contacts.secondaryContacts[index]
                const mspContact = mspContacts?.secondary[index]
                const isEmptyWithDifference =
                  !contact &&
                  secondaryContactsDifference > 0 &&
                  index >= contacts.secondaryContacts.length &&
                  index < contacts.secondaryContacts.length + secondaryContactsDifference
                return (
                  <Grid size={4} key={index}>
                    <ContactCard
                      data-testid={`secondary-contact-${index}`}
                      label={`Secondary Contact ${index + 1}`}
                      contactName={getSecondaryContactName(contact)}
                      contactPhoneNumber={formatPhoneNumberForDisplay(contact?.phone?.number, phoneCountry)}
                      isProfessionallyMonitored={isProfessionallyMonitored(location)}
                      additionalContactTooltip={
                        isEmptyWithDifference && mspContact
                          ? `${mspIdentifier} has an additional contact: ${mspContact.name} (${mspContact.phone.number})`
                          : undefined
                      }
                      shouldReserveEmptyPrimaryNameLine
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Stack>
        </Stack>
      )}
      <InterruptionPanel
        open={showNavigationInterruption}
        prompt="Exit Editing"
        details="All progress will be discarded if you leave now."
        firstOption="Exit"
        secondOption="Stay Here"
        onClose={() => handleNavigationInterruption(false)}
        onSelection={handleNavigationInterruption}
      />
    </>
  )
}
